import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, apiFetch } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Event as EventSchema, UpdateEventInput } from '@tunodo/ticketing-shared';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

export const Route = createFileRoute('/_app/admin/event-settings')({
  beforeLoad: () => {
    const { user } = useAuthStore.getState();
    if (user?.role !== 'ORGANIZER') {
      toast.info('Solo los organizadores pueden acceder a la configuración del evento.');
      throw redirect({ to: '/' });
    }
  },
  component: EventSettingsPage,
});

// Uses the ticketing-events endpoint (same as dashboard) to get the event list
const TicketingEventSchema = z.array(
  z.object({ id: z.string(), name: z.string(), date: z.string() }),
);

function EventSettingsPage() {
  const {
    data: events,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['ticketing-events'],
    queryFn: () =>
      apiFetch('/api/passport-admin/ticketing-events', { schema: TicketingEventSchema }),
    staleTime: 60_000,
  });

  const event = events?.[0];

  const { data: eventDetail, isLoading: isLoadingDetail } = useQuery({
    queryKey: ['event-detail', event?.id],
    queryFn: () => apiFetch(`/api/events/${event!.id}`, { schema: EventSchema }),
    enabled: !!event?.id,
    staleTime: 60_000,
  });

  const [emailSubject, setEmailSubject] = useState('');

  useEffect(() => {
    if (eventDetail) {
      setEmailSubject(eventDetail.emailSubject ?? '');
    }
  }, [eventDetail]);

  const mutation = useMutation({
    mutationFn: (body: UpdateEventInput) =>
      apiFetch(`/api/events/${event!.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      toast.success('Configuración guardada.');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Error al guardar la configuración');
    },
  });

  const handleSubmit = () => {
    if (!event) return;
    const body: UpdateEventInput = {
      emailSubject: emailSubject.trim() || null,
    };
    mutation.mutate(body);
  };

  if (isLoading || isLoadingDetail) {
    return (
      <main className="flex-1 p-6 space-y-6 max-w-lg">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-24" />
      </main>
    );
  }

  if (isError || !event) {
    return (
      <main className="flex-1 p-6">
        <p className="text-destructive text-sm">No se pudo cargar la configuración del evento.</p>
      </main>
    );
  }

  return (
    <main className="flex-1 p-6 space-y-6 max-w-lg">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Configuración del evento</h1>
        <p className="text-muted-foreground mt-1 text-sm">{event.name}</p>
      </div>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email-subject">Asunto del correo de boleta</Label>
          <Input
            id="email-subject"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            disabled={mutation.isPending}
            placeholder={`Tu boleta para ${event.name}`}
          />
          <p className="text-xs text-muted-foreground">
            Si lo dejas vacío, se usará: "Tu boleta para {event.name}"
          </p>
        </div>

        <Button onClick={handleSubmit} disabled={mutation.isPending}>
          {mutation.isPending ? 'Guardando…' : 'Guardar'}
        </Button>
      </div>
    </main>
  );
}
